"use client";

import { useEffect, useRef, useState } from "react";

import { isFinished, useJob } from "@/components/jobs/use-job";
import { ApiError, api, apiDownload } from "@/lib/api/client";
import type { Job } from "@/lib/api/types";
import { saveBlob } from "@/lib/download";

type Props = {
  briefingId: number;
  mainKeyword: string;
  /** Kompakte Variante für die Liste („PDF“). */
  compact?: boolean;
};

type Phase =
  | { kind: "idle" }
  | { kind: "running"; jobId: number }
  | { kind: "saving" }
  | { kind: "done"; filename: string }
  | { kind: "error"; message: string };

const BUTTON = "inline-flex h-11 items-center rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60";

/** Fortschritt des Exportjobs (3 Teilschritte), meldet das Ende genau einmal. */
function ExportRun({ jobId, onFinished }: { jobId: number; onFinished: (job: Job) => void }) {
  const { job, error } = useJob(jobId);
  const notified = useRef(false);

  useEffect(() => {
    if (job && isFinished(job) && !notified.current) {
      notified.current = true;
      onFinished(job);
    }
  }, [job, onFinished]);
  const done = job?.steps.filter((s) => s.status === "done").length ?? 0;
  const total = job?.steps.length ?? 3;
  return (
    <span role="status" className="text-[13px] text-muted">
      {error ?? `PDF wird erstellt: Schritt ${Math.min(done + 1, total)} von ${total}${job ? ` · ${job.steps[Math.min(done, total - 1)]?.title ?? ""}` : ""}`}
    </span>
  );
}

/**
 * „PDF exportieren“: startet den Export als Job (kostenlos, nur gespeicherte Daten), zeigt den
 * Fortschritt und lädt die Datei am Ende herunter (SPEC 4.1 „PDF-Export“).
 */
export function PdfExportButton({ briefingId, mainKeyword, compact = false }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });

  const start = async () => {
    setPhase({ kind: "saving" });
    try {
      const job = await api<Job>(`/api/briefings/${briefingId}/pdf`, { method: "POST" });
      setPhase({ kind: "running", jobId: job.id });
    } catch (caught) {
      setPhase({ kind: "error", message: caught instanceof Error ? caught.message : "Unbekannter Fehler" });
    }
  };

  const finished = async (job: Job) => {
    if (job.status !== "succeeded") {
      const message = job.status === "cancelled" ? "Der Export wurde abgebrochen." : (job.error ?? "Der Export ist fehlgeschlagen.");
      setPhase({ kind: "error", message });
      return;
    }
    setPhase({ kind: "saving" });
    try {
      const { blob, filename } = await apiDownload(`/api/briefings/${briefingId}/pdf`, `briefing-${briefingId}.pdf`);
      saveBlob(blob, filename);
      setPhase({ kind: "done", filename });
    } catch (caught) {
      const message = caught instanceof ApiError || caught instanceof Error ? caught.message : "Unbekannter Fehler";
      setPhase({ kind: "error", message });
    }
  };

  const busy = phase.kind === "running" || phase.kind === "saving";
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => void start()}
        disabled={busy}
        aria-label={compact ? `PDF exportieren: ${mainKeyword}` : undefined}
        title="Kostenlos: nutzt nur die gespeicherten Daten"
        className={BUTTON}
      >
        {compact ? "PDF" : phase.kind === "error" ? "Erneut versuchen" : "PDF exportieren"}
      </button>
      {phase.kind === "running" && <ExportRun key={phase.jobId} jobId={phase.jobId} onFinished={(job) => void finished(job)} />}
      {phase.kind === "saving" && (
        <span role="status" className="text-[13px] text-muted">
          PDF wird heruntergeladen …
        </span>
      )}
      {phase.kind === "done" && (
        <span role="status" className="text-[13px] text-success">
          PDF gespeichert: {phase.filename}
        </span>
      )}
      {phase.kind === "error" && (
        <span role="alert" className="text-[13px] text-danger">
          {phase.message}
        </span>
      )}
    </span>
  );
}
